import { ReduxEnhancedAuthProvider } from 'HOC/withAuth';
import { useMutationConsentDirector } from 'modules/Business/services/directors/queries';
import { useState, cloneElement, FunctionComponent, MouseEventHandler } from 'react';

import { Box, Button, IconChevronLeft, GridedContent, Layout } from '@capitalise/ui-design-system';
import DirectorConsentForm, { DirectorFormData } from './DirectorConsentForm';
import DirectorConsentError from './DirectorConsentError';
import DirectorConsentSuccess from './DirectorConsentSuccess';
import { dateFormat } from 'utils/DateHelper';

export interface DirectorConsentOnboardingProps {
  title: string;
  lead: string;
  hasPrevious: boolean;
  image: FunctionComponent<{ width: string; height: string }>;
  onPreviousClick?: MouseEventHandler<HTMLButtonElement>;
  children?: JSX.Element;
  completeOnboarding?: () => void;
}

enum Steps {
  FORM,
  ERROR,
  SUCCESS,
}

const DirectorConsentOnboarding = ({
  hasPrevious,
  onPreviousClick,
  children,
  ...options
}: DirectorConsentOnboardingProps) => {
  const [currentStep, setCurrentStep] = useState(Steps.FORM);
  const { mutate: postDirectorConsent, isLoading, isError, error } = useMutationConsentDirector();
  const onSubmit = (data: DirectorFormData) => {
    const payload = {
      directorPersonalData: {
        forename: data.name.split(' ')[0],
        surname: data.name.split(' ')[1],
        birthDate: dateFormat(new Date(data.dateOfBirth), 'yyyy-MM-dd'),
      },
      directorAddressData: {
        houseNumber: data.houseNumber.replace(/,/g, ''),
        street: data.street.replace(/,/g, ''),
        city: data.town.replace(/,/g, ''),
        postalCode: data.postCode.replace(/,/g, ''),
      },
    };

    postDirectorConsent(payload, {
      onSuccess: () => {
        setCurrentStep(Steps.SUCCESS);
      },
      onError: () => {
        setCurrentStep(Steps.ERROR);
      },
    });
  };

  const handleBackError = () => {
    setCurrentStep(Steps.FORM);
  };

  return (
    <Layout layoutProps={{ overflowY: 'auto', backgroundColor: 'white' }}>
      <ReduxEnhancedAuthProvider>
        <GridedContent>
          <Box
            gridColumn={{ _: '1/5', sm: '3/7', lg: '5/9' }}
            display="flex"
            flexDirection="column"
          >
            {currentStep === Steps.FORM && hasPrevious && (
              <Box display="flex" alignSelf="start" pt={{ _: 'xsmall', md: 'medium' }}>
                <Button
                  baseColor="green"
                  leftIcon={<IconChevronLeft />}
                  onClick={onPreviousClick}
                  size="small"
                  variant="text"
                >
                  Previous
                </Button>
              </Box>
            )}
            {/* Using display prevent form to be clear in case of error and user needs to return */}
            <Box display={currentStep === Steps.FORM ? 'block' : 'none'}>
              <DirectorConsentForm
                onSubmit={onSubmit}
                isError={isError}
                error={error}
                isLoading={isLoading}
                origin="onboardingBusiness"
                prev={options?.completeOnboarding}
              />
            </Box>
            {currentStep === Steps.ERROR && <DirectorConsentError onBack={handleBackError} />}
            {currentStep === Steps.SUCCESS && (
              <DirectorConsentSuccess completeOnboarding={options?.completeOnboarding} />
            )}
            {children && <Box pb="xsmall">{cloneElement(children, options)}</Box>}
          </Box>
        </GridedContent>
      </ReduxEnhancedAuthProvider>
    </Layout>
  );
};

export default DirectorConsentOnboarding;
